import {Component, DoCheck, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {DateRange} from '@domain/date-range';
import {MachineType} from '@domain/machine/machine-type.enum';
import {Regime} from '@domain/machine/regime';
import {Shift} from '@domain/machine/shift';
import {CalendarItem} from '@domain/planning/calendar-item';
import {CalendarItemGroup} from '@domain/planning/calendar-item-group';
import {PlanningMachineCarpetRun} from '@domain/planning/carpet/planning-machine-carpet-run';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningMachineRun} from '@domain/planning/planning-machine-run';
import {PlanningOrder} from '@domain/planning/planning-order';
import {PlanningMachineTuftingRun} from '@domain/planning/tufting/planning-machine-tufting-run';
import {PlanningMachineWeavingRun} from '@domain/planning/weaving/planning-machine-weaving-run';
import {OnboardingPlanningStep} from '@presentation/components/onboarding-dialog/onboarding-planning-step.enum';
import {
  AssertionUtils,
  ColorConstants,
  DateTimeFormat,
  DateTimePipe,
  DragDropData,
  MACHINE_GROUP_COLOURS_PALETTE,
  PlanningDisplayComponent,
  PlanningGridComponent,
  TranslateService
} from '@vdw/angular-component-library';
import {isEqual} from 'lodash-es';
import {DateTime} from 'luxon';
import moment from 'moment';
import {NavigationPlanningData} from '../navigation-planning-data.type';
import {PlanningScrollPositionData} from '../planning-scroll-position-data.interface';
import {PlanningContextService} from '../planning-services/planning-context.service';
import {PlanningDragDropChangesService} from '../planning-services/planning-drag-drop-changes.service';
import {PlanningOnboardingService} from '../planning-services/planning-onboarding.service';
import {PlanningComponent} from '../planning.component';

@Component({
  selector: 'app-planning-display-content',
  templateUrl: './planning-display-content.component.html',
  styleUrls: ['./planning-display-content.component.scss']
})
export class PlanningDisplayContentComponent implements OnInit, DoCheck, OnDestroy {
  @Input() public cellsPerDay: number;
  @Input() public regimes: Regime[] = [];
  @Input() public selectedDateRange: DateRange;
  @Input() public fallbackMachineGroupId: number;

  @ViewChild(PlanningGridComponent)
  public planningGrid: PlanningGridComponent;

  @ViewChild(PlanningDisplayComponent)
  public planningDisplay: PlanningDisplayComponent;

  private isFirstChanges = true;
  private readonly UNKNOWN_OBJECT_KEY = 'PLANNING.UNKNOWN';

  public constructor(
    public readonly planning: PlanningComponent,
    private readonly translate: TranslateService,
    public readonly planningContext: PlanningContextService,
    private readonly planningOnboarding: PlanningOnboardingService,
    public readonly dragDropChanges: PlanningDragDropChangesService,
    private readonly navigationHelperService: NavigationHelperService<NavigationPlanningData>,
    private readonly dateTimePipe: DateTimePipe
  ) {}

  public ngOnInit(): void {
    const emptyScrollPositionState = {queryName: null, scrollTop: null, scrollLeft: null} as PlanningScrollPositionData;
    const scrollPositionState = this.navigationHelperService.getPartialState<PlanningScrollPositionData>(Object.keys(emptyScrollPositionState));

    if (scrollPositionState && !isEqual(emptyScrollPositionState, scrollPositionState)) {
      const scrollContainer = document.querySelector(scrollPositionState.queryName);

      setTimeout(() => {
        scrollContainer.scrollTo({top: scrollPositionState.scrollTop, left: scrollPositionState.scrollLeft});
      });
    }
  }

  public ngDoCheck(): void {
    if (this.isFirstChanges && this.planningGrid?.bodyContainer != null) {
      this.isFirstChanges = false;
      this.planningContext.planningGridDisplay = this.planningGrid;
      this.planningGrid.bodyContainerOffset = this.planningGrid.bodyContainer.nativeElement.getBoundingClientRect();
    }
  }

  public ngOnDestroy(): void {
    const scrollContainerQueryName = '.scroll-container';
    const scrollContainer = document.querySelector(scrollContainerQueryName);
    if (scrollContainer) {
      this.navigationHelperService.savePartialState<PlanningScrollPositionData>({queryName: scrollContainerQueryName, scrollTop: scrollContainer.scrollTop, scrollLeft: scrollContainer.scrollLeft});
    }
  }

  public trackByRunId(_: number, group: PlanningMachineRun): number {
    return group.id;
  }

  public trackByOrderId(_: number, order: PlanningOrder): number {
    return order.id;
  }

  public trackByPlanningItemId(index: number, item: CalendarItem): number {
    return item.id;
  }

  public getMachineColor(machine: PlanningMachine): string {
    return !AssertionUtils.isNullOrUndefined(this.fallbackMachineGroupId)
      ? MACHINE_GROUP_COLOURS_PALETTE[(machine.group?.id ?? this.fallbackMachineGroupId) % MACHINE_GROUP_COLOURS_PALETTE.length](1)
      : '';
  }

  public getMachineIcon(machine: PlanningMachine): string {
    return MachineType.getIconNameForMachineType(machine.machineType);
  }

  public getMachineName(machine: PlanningMachine): string {
    return machine.name ?? this.translate.instant('GENERAL.NO_DATA', {object: this.translate.instant('GENERAL.NAME').toLowerCase()});
  }

  public getShiftName(shift: Shift, regime: Regime): string {
    return `Shift: ${shift.name} ${this.dateTimePipe.transform(DateTime.fromISO(shift.shiftStart).toJSDate(), DateTimeFormat.TIME_SIMPLE)} - ${this.dateTimePipe.transform(DateTime.fromISO(shift.shiftEnd).toJSDate(), DateTimeFormat.TIME_SIMPLE)} (regime: ${regime.name})`;
  }

  public getRunsForMachineId(machineId: number): PlanningMachineRun[] {
    const groupedItemsForMachine = this.planningContext.groupedCalendarItems.find((group: CalendarItemGroup) => group.machineId === machineId);
    const runsForMachine = groupedItemsForMachine?.items?.filter((item: CalendarItem) => item instanceof PlanningMachineRun) as PlanningMachineRun[];

    return this.planningContext.hasMachineMachineRunPlaceholder(machineId) ? [...runsForMachine, this.planningContext.selectedPlaceholder as PlanningMachineRun] : runsForMachine;
  }

  public getPlanningItemsForMachineId(machineId: number): CalendarItem[] {
    const groupedItemsForMachine = this.planningContext.groupedCalendarItems.find((group: CalendarItemGroup) => group.machineId === machineId);
    const planningItemsForMachine = groupedItemsForMachine?.items?.filter((item: CalendarItem) => !(item instanceof PlanningMachineRun));

    return this.planningContext.hasMachinePlanningItemPlaceholder(machineId) ? [...planningItemsForMachine, this.planningContext.selectedPlaceholder] : planningItemsForMachine;
  }

  public shouldOrderShowRightBorder(run: PlanningMachineRun, order: PlanningOrder, orderIndex: number): boolean {
    return orderIndex !== run.planningOrders.length - 1 && order.endDate < this.selectedDateRange[1];
  }

  public isPlanningItemCompleted(planningItem: PlanningItem): boolean {
    return this.planningContext.getCurrentDateForSubscriptionTimezone() >= planningItem.endDate;
  }

  public hasRunOrdersPastDueDate(run: PlanningMachineRun): boolean {
    return run.planningOrders?.some((order: PlanningOrder) => !order.productionOrder.completed && order.isProductionOrderDueDateBeforeEndDate);
  }

  public hasRunBeenCompleted(run: PlanningMachineRun): boolean {
    const notCompletedOrder = run.planningOrders?.find((order: PlanningOrder) => !order.productionOrder.completed);

    return !AssertionUtils.isEmpty(run.planningOrders) && AssertionUtils.isNullOrUndefined(notCompletedOrder);
  }

  public getRowBackgroundColor(machine: PlanningMachine): string {
    return machine.id === this.planningOnboarding.DEMO_ITEM_ID && this.planningOnboarding.currentOnboardingStep === OnboardingPlanningStep.ADD_ITEM
      ? ColorConstants.TINT_DARKBLUE_6
      : ColorConstants.PRIMARY_COLOR_WHITE;
  }

  public getOrderClass(order: PlanningOrder): string {
    let orderClass = 'planning-order';
    if (order.productionOrder.completed) {
      orderClass += ' completed';
    }
    if (order.isProductionOrderDueDateBeforeEndDate) {
      orderClass += ' past-due';
    }

    return orderClass;
  }

  public getRunTitle(run: PlanningMachineRun): string {
    if (run instanceof PlanningMachineCarpetRun) {
      return this.getCarpetRunTitle(run);
    }
    if (run instanceof PlanningMachineWeavingRun) {
      return this.getWeavingRunTitle(run);
    }
    if (run instanceof PlanningMachineTuftingRun) {
      return this.getTuftingRunTitle(run);
    }
  }

  public isRunDemoItem(run: PlanningMachineRun): boolean {
    return this.planningContext.isOnboardingRunning && run.id === this.planningOnboarding.DEMO_ITEM_ID;
  }

  public getNewRunText(): string {
    return this.translate.instant('GENERAL.ACTIONS.NEW_OBJECT', {object: 'PLANNING.ADD_ITEM.TYPES.RUN'}).toUpperCase();
  }

  public onPlanningItemDragStart(dragDropData: DragDropData): void {
    this.planning.planningSideBars.closeSidebar(false);
    this.dragDropChanges.onPlanningItemDragStart(dragDropData);
  }

  public onPlanningOrderDragStart(dragDropData: DragDropData): void {
    this.planning.planningSideBars.closeSidebar(false);
    this.dragDropChanges.onPlanningOrderDragStart(dragDropData);
  }

  public onGridDisplaySelected({event, rowIndex, date}: {event: PointerEvent; rowIndex: number; date: Date}): void {
    if (!this.planningContext.canEdit()) {
      return;
    }

    const placeholderEndDate = moment(date).add(1, 'h').toDate();
    const group = this.planningContext.groupedCalendarItems?.find((itemGroup: CalendarItemGroup) => itemGroup.machineId === this.planningContext.machinesForSelectedPlanningGroups[rowIndex]?.id);
    const overlappingItem = group?.items.find((item: CalendarItem) => item.startDate <= date && item.endDate >= placeholderEndDate);

    if (overlappingItem == null && !this.planningContext.isOnboardingRunning) {
      this.planning.addPlaceholder({event, rowIndex, date});
    }
  }

  private getTuftingRunTitle(run: PlanningMachineTuftingRun): string {
    if (run.isNew() && !run.tuftProduct) {
      return this.getNewRunText();
    }
    return (run.tuftProduct?.name ?? this.translate.instant(this.UNKNOWN_OBJECT_KEY)).toUpperCase();
  }

  private getWeavingRunTitle(run: PlanningMachineWeavingRun): string {
    if (run.isNew() && !run.weaveProduct) {
      return this.getNewRunText();
    }
    return (run.weaveProduct?.name ?? this.translate.instant(this.UNKNOWN_OBJECT_KEY)).toUpperCase();
  }

  private getCarpetRunTitle(run: PlanningMachineCarpetRun): string {
    if (run.isNew() && !run.machineQuality && !run.creel) {
      return this.getNewRunText();
    }

    const creelLabel = run.creel?.name ?? this.translate.instant(this.UNKNOWN_OBJECT_KEY);
    const machineQualityLabel = run.machineQuality?.technicalName ?? this.translate.instant(this.UNKNOWN_OBJECT_KEY);

    return `${machineQualityLabel}/${creelLabel}`.toUpperCase();
  }
}
